import { useState, useRef, useCallback } from "react";
import { Paperclip, ArrowUp, Square, X } from "lucide-react";
import { useChatInputHistory } from "@/hooks/useChatInputHistory";
import type { ChatAttachment } from "@/types/content-blocks";
import { MAX_ATTACHMENT_BYTES, getFileType } from "@/types/content-blocks";

interface Props {
  onSend: (message: string, attachments: ChatAttachment[]) => void;
  onStop?: () => void;
  isStreaming: boolean;
  disabled: boolean;
}

export function ChatInput({ onSend, onStop, isStreaming, disabled }: Props) {
  const { value, setValue, onKeyDown: historyKeyDown, commit } = useChatInputHistory();
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [attachments, setAttachments] = useState<ChatAttachment[]>([]);

  const handleSend = useCallback(() => {
    const text = value.trim();
    if (!text && attachments.length === 0) return;
    if (disabled || isStreaming) return;

    onSend(text, [...attachments]);
    commit();
    setAttachments([]);

    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  }, [value, attachments, disabled, isStreaming, onSend, commit]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
      return;
    }
    historyKeyDown(e);
  }, [handleSend, historyKeyDown]);

  const handleInput = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 200)}px`;
    }
  };

  const processFile = useCallback((file: File) => {
    if (file.size > MAX_ATTACHMENT_BYTES) return;

    const reader = new FileReader();
    const fileType = getFileType(file.name);

    if (fileType === "image") {
      reader.onload = () => {
        const att: ChatAttachment = {
          id: crypto.randomUUID(),
          name: file.name,
          type: "image",
          content: reader.result as string,
          sizeBytes: file.size,
        };
        setAttachments((prev) => [...prev, att]);
      };
      reader.readAsDataURL(file);
    } else {
      reader.onload = () => {
        const att: ChatAttachment = {
          id: crypto.randomUUID(),
          name: file.name,
          type: "text",
          content: reader.result as string,
          sizeBytes: file.size,
        };
        setAttachments((prev) => [...prev, att]);
      };
      reader.readAsText(file);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    for (const file of Array.from(e.dataTransfer.files)) {
      processFile(file);
    }
  }, [processFile]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    for (const file of Array.from(e.target.files ?? [])) {
      processFile(file);
    }
    e.target.value = "";
  }, [processFile]);

  const removeAttachment = useCallback((id: string) => {
    setAttachments((prev) => prev.filter((a) => a.id !== id));
  }, []);

  const canSend = (value.trim().length > 0 || attachments.length > 0) && !disabled;

  return (
    <div className="shrink-0">
      <div className="max-w-[720px] mx-auto px-5 py-3">
        {attachments.length > 0 && (
          <div className="flex gap-2 mb-2">
            {attachments.map((att) => (
              <div key={att.id} className="relative group">
                {att.type === "image" ? (
                  <div className="w-11 h-11 rounded-md border border-colony-border-subtle overflow-hidden bg-colony-bg">
                    <img src={att.content} alt={att.name} className="w-full h-full object-cover" />
                  </div>
                ) : (
                  <div className="h-11 rounded-md border border-colony-border-subtle bg-colony-bg flex items-center gap-1.5 px-2.5">
                    <span className="text-[10px] font-mono text-colony-text-secondary max-w-[80px] truncate">{att.name}</span>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => removeAttachment(att.id)}
                  className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-colony-surface-active text-colony-text-primary flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                >
                  <X size={8} strokeWidth={2.5} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div
          className="rounded-xl border border-colony-border bg-colony-surface focus-within:border-colony-border-hover transition-colors"
          onDrop={handleDrop}
          onDragOver={(e) => e.preventDefault()}
        >
          <textarea
            ref={textareaRef}
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onInput={handleInput}
            disabled={disabled}
            placeholder="Message..."
            rows={1}
            className="w-full bg-transparent text-[13px] text-colony-text-primary placeholder:text-colony-text-tertiary outline-none resize-none leading-[1.5] min-h-[40px] max-h-[200px] px-4 pt-2.5 pb-1 disabled:opacity-50"
            style={{ scrollbarWidth: "thin", scrollbarColor: "#2A2A2F transparent" }}
          />
          <div className="flex items-center justify-between px-3 pb-2">
            <div className="flex items-center">
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                aria-label="Attach file"
                className="flex items-center justify-center w-7 h-7 rounded-lg text-colony-text-tertiary hover:text-colony-text-secondary hover:bg-white/[0.04] transition-colors cursor-pointer"
              >
                <Paperclip size={14} strokeWidth={1.5} />
              </button>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                className="hidden"
                onChange={handleFileSelect}
              />
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[10px] text-colony-text-tertiary/50 mr-1">
                <kbd className="text-colony-text-tertiary">Shift+Enter</kbd> newline
              </span>
              {isStreaming ? (
                <button
                  type="button"
                  onClick={onStop}
                  className="w-7 h-7 rounded-lg bg-red-500/15 text-red-400 flex items-center justify-center cursor-pointer hover:bg-red-500/25 transition-colors"
                  title="Stop"
                >
                  <Square className="w-3 h-3" fill="currentColor" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleSend}
                  disabled={!canSend}
                  className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all ${
                    canSend
                      ? "bg-colony-brand text-white cursor-pointer hover:bg-colony-brand-hover"
                      : "bg-white/[0.06] text-colony-text-tertiary"
                  }`}
                >
                  <ArrowUp className="w-4 h-4" strokeWidth={2.5} />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
